import { z } from "zod";

// ---------------------------------------------------------------------------
// home-buying-cost — the upfront "true cost of buying" COMPOSER.
//
// This module owns NO calculation of duty / LMI / scheme / FHSS logic. It is a
// pure ASSEMBLER that calls the four shipped modules' public functions and
// stitches their cited outputs into one itemised "cash needed to complete"
// picture for a NSW / VIC / QLD first-or-next home buyer:
//
//   purchase price
//     → transfer duty            (stamp-duty: calculateFirstHomeDuty / Transfer)
//     + LMI                       (lmi: calculateLmiCost; $0 if FHBG waiver)
//     + other upfront costs       (conveyancing / inspection / registration —
//                                  CITED indicative RANGES, our own dated table)
//     − government help           (FHOG via first-home-scheme; duty concession is
//                                  already reflected in the duty line)
//   …and SURFACE the FHSS releasable amount (fhss) as a deposit contribution.
//
// Output is an itemised, fully-sourced breakdown where every line is traceable
// to its source module + citation. The COMPOSER itself only carries its own
// dated constants (the misc-cost indicative ranges), so it ships as a registry
// triad just like the leaf modules.
//
// REGULATORY POSTURE (inherited + composite — binding):
//  - The four children each carry their own NCCP / indicative / disqualifier-led
//    guards. This composer PROPAGATES them and adds its OWN composite guard:
//    a personalised total-cash-to-buy figure is EXACTLY the surface a paid
//    credit handoff must never ride alongside (dealing-by-arranging, the C2
//    kill-line). `assertNoCreditHandoff` runs over the assembled output AND over
//    any surface placements — tested.
//  - Information, not advice. The output NEVER says "you can afford", "you save",
//    "you should buy". It is a sum of CITED line items with a difference figure
//    framed as the difference between two cited amounts.
//  - LMI rides as an INDICATIVE RANGE (never a quote) — the `lmiIndicative`
//    flag and the range shape are preserved end-to-end.
//  - The FHSS line carries the low-income-erosion / "ATO finalises" honesty
//    caveats where relevant; it is surfaced as a deposit CONTRIBUTION (the
//    buyer's own super), never netted into the completion-cash figure as if it
//    were a grant.
//  - Mandatory disclaimer rendered on every surface (see copy.ts).
// ---------------------------------------------------------------------------

/** v1 jurisdictions — the three with verified child tables (NSW / VIC / QLD). */
export const HBC_STATES = ["NSW", "VIC", "QLD"] as const;
export type HbcState = (typeof HBC_STATES)[number];

/** Established vs new build — drives the QLD new-build duty branch + FHOG gate. */
export const HBC_BUILD_TYPES = ["established", "new"] as const;
export type HbcBuildType = (typeof HBC_BUILD_TYPES)[number];

/** Buyer residency — drives the foreign-purchaser surcharge line in stamp-duty. */
export const HBC_RESIDENCY = ["domestic", "foreign"] as const;
export type HbcResidency = (typeof HBC_RESIDENCY)[number];

// ===========================================================================
// Input
// ===========================================================================

/**
 * FHSS releasable inputs, surfaced as a deposit contribution. Optional: when
 * absent we don't compute an FHSS line (the buyer hasn't made voluntary
 * contributions). All fields mirror the fhss module's eligibility surface so
 * we can call it without re-deriving anything.
 */
export const HbcFhssInputSchema = z.object({
  /** Age at the intended FHSS determination request. Gate: ≥ 18. */
  ageAtDetermination: z.number().int().min(0),
  /** First home buyer (the first-home gate). */
  firstHome: z.boolean().default(true),
  /** Has ever owned AU property before (the never-owned gate). */
  hasOwnedAuPropertyBefore: z.boolean().default(false),
  /** Hardship exception asserted (prior ownership doesn't fail the gate). */
  financialHardshipException: z.boolean().default(false),
  /** Has ever made a valid FHSS release before (one-shot gate). */
  hasPreviouslyReleased: z.boolean().default(false),
  /** Voluntary CONCESSIONAL contributions made this FY (excl. employer SG). */
  concessionalThisYear: z.number().min(0).default(0),
  /** Voluntary NON-CONCESSIONAL (after-tax) contributions made this FY. */
  nonConcessionalThisYear: z.number().min(0).default(0),
  /** FHSS-eligible voluntary contributions counted in PRIOR years. */
  priorYearsCounted: z.number().min(0).default(0),
});
export type HbcFhssInput = z.input<typeof HbcFhssInputSchema>;

export const HomeBuyingCostInputSchema = z.object({
  /** State the property is in. v1 = NSW / VIC / QLD. */
  state: z.enum(HBC_STATES),
  /** Purchase price / dutiable value, whole dollars. */
  purchasePrice: z.number().positive(),
  /** Deposit the buyer has saved, whole dollars. Drives LVR + the 5% scheme gate. */
  deposit: z.number().min(0),
  /** Established vs new build. */
  buildType: z.enum(HBC_BUILD_TYPES).default("established"),
  /** Buyer residency — foreign adds the surcharge line. */
  residency: z.enum(HBC_RESIDENCY).default("domestic"),
  /** Genuine first home buyer (no prior ownership). Gates concessions/grants. */
  firstHomeBuyer: z.boolean().default(true),
  /**
   * Combined annual income — retained for the 5% scheme surface (caps removed
   * 1 Oct 2025, but historical assessments still test it). Default 0 (untested).
   */
  income: z.number().min(0).default(0),
  /**
   * Single parent / guardian buying alone — unlocks the 2% Family Home Guarantee
   * deposit floor on the 5% scheme.
   */
  singleParentOrGuardian: z.boolean().default(false),
  /** Optional FHSS releasable inputs — surfaced as a deposit contribution. */
  fhss: HbcFhssInputSchema.optional(),
  /**
   * The assessment DATE (ISO YYYY-MM-DD). Passed in (never the clock) so a
   * backdated contract resolves against the scheme period + FY rulesets in force
   * then. The FY rulesets (duty/LMI/FHSS) are keyed off this date's FY.
   */
  assessmentDate: z.string(),
}).refine((v) => v.deposit <= v.purchasePrice, {
  message: "deposit cannot exceed purchase price",
  path: ["deposit"],
});
/** Caller-facing input — defaulted fields stay optional (z.input). */
export type HomeBuyingCostInput = z.input<typeof HomeBuyingCostInputSchema>;

// ===========================================================================
// Composer ruleset — its OWN dated constants (the misc-cost indicative ranges)
// ===========================================================================

/** An indicative dollar range. The composer never collapses a range to a point. */
export interface IndicativeRange {
  low: number;
  high: number;
}

/** One indicative misc-cost line item, with its own source + dated stamp. */
export interface MiscCostItem {
  /** Stable id, e.g. "conveyancing". */
  id: "conveyancing" | "building_pest_inspection" | "mortgage_registration" | "transfer_registration";
  /** Human label, e.g. "Conveyancing / legal". */
  label: string;
  /** INDICATIVE cost range, AUD — never a precise quote. */
  range: IndicativeRange;
  /** Source-of-truth URL the range was transcribed from. */
  source: string;
}

/**
 * The composer's own versioned ruleset: the indicative misc-cost ranges, keyed
 * per FY and dated. These are the ONLY constants this module owns — every other
 * figure comes from a child module. Registry triad, just like the leaf modules.
 */
export interface HomeBuyingCostRuleSet {
  /** Engine tag — distinguishes the composer ruleset from the AU0x products. */
  engine: "home-buying-cost";
  /** Financial-year key, e.g. "2025-26". */
  fy: string;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Date the misc-cost sources were last verified, ISO. */
  sourcesVerifiedAt: string;
  /**
   * Indicative misc upfront costs that apply in ALL states (conveyancing,
   * inspection). Each an INDICATIVE RANGE with a source — NOT a precise quote.
   */
  miscCommon: readonly MiscCostItem[];
  /**
   * Per-state government registration / transfer fees (Land Titles Office /
   * Land Registry). Indicative ranges — fees change on state schedules.
   */
  miscByState: Record<HbcState, readonly MiscCostItem[]>;
}

// ===========================================================================
// Output — itemised, fully-sourced breakdown
// ===========================================================================

/** A single line of the itemised breakdown, traceable to its source module. */
export interface CostLine {
  /** Stable id of the line. */
  id: string;
  /** Human label, e.g. "Transfer (stamp) duty". */
  label: string;
  /**
   * The line's contribution to the completion cash. POSITIVE = cash out
   * (duty, LMI, misc costs). NEGATIVE = government help reducing cash needed
   * (FHOG). For an indicative line this is the MIDPOINT of `range`; the range
   * carries the honest spread.
   */
  amount: number;
  /**
   * INDICATIVE range when the figure is not exact (LMI, misc costs). `null` for
   * exact figures (duty, FHOG). When present the UI must render the range with
   * its indicative stamp — `amount` is only the midpoint for the running total.
   */
  range: IndicativeRange | null;
  /** Whether this line is an indicative estimate (true) or an exact figure. */
  indicative: boolean;
  /** Which child module / authority produced this figure. */
  source: string;
  /** Plain, cited explanation of how the figure was derived. */
  note: string;
}

export interface HomeBuyingCostOutput {
  /** The assessment date the picture was assembled against (ISO). */
  assessmentDate: string;
  /** The FY the child rulesets resolved to (e.g. "2025-26"). */
  fy: string;
  /** Echoed inputs for the result surface. */
  state: HbcState;
  purchasePrice: number;
  deposit: number;
  buildType: HbcBuildType;
  /** Loan-to-value ratio implied by price − deposit (0 when fully cash). */
  lvr: number;

  // ── Cost lines (cash OUT), each traceable to a child module ──
  /** Transfer (stamp) duty payable, after any first-home concession. Exact. */
  dutyLine: CostLine;
  /** Indicative LMI cost ($0 when FHBG waiver or LVR ≤ 80%). */
  lmiLine: CostLine;
  /** Indicative misc upfront costs (conveyancing/inspection/registration). */
  miscLines: readonly CostLine[];

  // ── Government help (cash IN / reduces cash needed) ──
  /** First Home Owner Grant, as a NEGATIVE line ($0 when ineligible). Exact. */
  fhogLine: CostLine;

  // ── Totals (exact band, never a single quote because LMI/misc are ranges) ──
  /**
   * Total cash needed to COMPLETE = duty + LMI + misc − FHOG, as a RANGE because
   * LMI and misc are indicative. `low`/`high` carry the honest spread; there is
   * NO single "your cost is $X" because two inputs are genuine ranges.
   */
  totalCashToComplete: IndicativeRange;
  /** The midpoint of `totalCashToComplete` — for sorting/ordering ONLY, never a quote. */
  totalCashMidpoint: number;

  // ── FHSS deposit contribution (the buyer's OWN super, surfaced not netted) ──
  /**
   * FHSS releasable amount as a deposit contribution, or `null` when no FHSS
   * inputs were supplied / the buyer is ineligible. SURFACED separately — it is
   * the buyer's own super, NOT government help, so it never reduces the
   * completion-cash figure above.
   */
  fhssDepositContribution: {
    /** Whether the buyer appears to pass the FHSS gates. */
    eligible: boolean;
    /** Max releasable voluntary contributions (net of caps + 85% haircut). */
    maxReleasableContributions: number;
    /** ATO-deemed earnings are added on release (a fact, not computed). */
    earningsAddedOnRelease: boolean;
    /** Low-income-erosion caveat applies to the concessional contribution leg. */
    lowIncomeErosionCaveat: boolean;
  } | null;

  /** The scheme-eligibility stacking notes from first-home-scheme (cross-sell moat). */
  stackingNotes: readonly string[];

  /** Hard guarantee for callers/UI: the total is an indicative band, not a quote. */
  isIndicativeTotal: true;
}
